import React, { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import EventIcon from '@mui/icons-material/Event'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useWalletSafe } from '../contexts/WalletContext'

const ORANGE = '#FF8A00'
const SURFACE = 'rgba(16,11,38,0.92)'
const BORDER = '1px solid rgba(255,255,255,0.08)'

const LOCAL_COUNTRIES = new Set(['SL', 'SIERRA LEONE', 'LR', 'LIBERIA', 'GN', 'GUINEA', 'GM', 'GAMBIA'])
const LOCAL_CURRENCY_BY_COUNTRY = {
    SL: 'SLE',
    'SIERRA LEONE': 'SLE',
    LR: 'LRD',
    LIBERIA: 'LRD',
    GN: 'GNF',
    GUINEA: 'GNF',
    GM: 'GMD',
    GAMBIA: 'GMD',
}

const normalizeCountry = (value) => String(value || '').trim().toUpperCase()
const money = (amount, currency) => `${currency} ${Number(amount || 0).toFixed(2)}`

const localCurrencyFor = (country, fallback = 'SLE') => (
    LOCAL_CURRENCY_BY_COUNTRY[normalizeCountry(country)] || fallback
)
const isLocalEventCountry = (country) => LOCAL_COUNTRIES.has(normalizeCountry(country))

const Row = ({ label, value, accent = false }) => (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography sx={{ color: 'rgba(255,255,255,0.66)', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ color: accent ? ORANGE : '#fff', fontWeight: accent ? 900 : 800, textAlign: 'right' }}>{value}</Typography>
    </Stack>
)

export default function Tickets() {
    const { user } = useAuth()
    const wallet = useWalletSafe()
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [buyingId, setBuyingId] = useState('')
    const [quantityById, setQuantityById] = useState({})
    const [success, setSuccess] = useState('')

    const userCountry = normalizeCountry(user?.country)
    const isDiaspora = userCountry ? !LOCAL_COUNTRIES.has(userCountry) : false
    const senderCurrency = isDiaspora
        ? String(user?.baseCurrency || 'USD').toUpperCase()
        : localCurrencyFor(userCountry || 'SL')

    const loadEvents = async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get('/events', {
                params: isDiaspora ? {} : { country: userCountry || 'SL' },
            })
            const rows = Array.isArray(response.data?.data) ? response.data.data : []
            setEvents(
                rows
                    .filter((event) => {
                        if (!event.starts_at && !event.date) return true
                        const date = new Date(event.starts_at || event.date)
                        return Number.isNaN(date.getTime()) || date >= new Date()
                    })
                    .sort((a, b) => {
                        const left = new Date(a.starts_at || a.date || '2999-12-31').getTime()
                        const right = new Date(b.starts_at || b.date || '2999-12-31').getTime()
                        return left - right
                    })
            )
        } catch (e) {
            setError(e?.response?.data?.message || 'Unable to load event tickets.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadEvents()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCountry, isDiaspora])

    const balance = wallet?.getBalance?.(senderCurrency) || 0

    const quoteFor = (event) => {
        const quantity = Number(quantityById[event.id] || 1)
        const eventCurrency = String(event.currency || localCurrencyFor(event.country)).toUpperCase()
        const ticketValue = Number(event.price || event.ticket_price || 0) * quantity
        const senderBase = isDiaspora && senderCurrency !== eventCurrency ? ticketValue : ticketValue
        const serviceFee = Number((senderBase * 0.05).toFixed(2))
        const transferFee = isDiaspora && isLocalEventCountry(event.country)
            ? Number((Math.max(senderBase * 0.0115 + 1.2, 1).toFixed(2)))
            : 0
        const fee = serviceFee + transferFee
        return {
            quantity,
            eventCurrency,
            ticketValue,
            senderBase,
            fee,
            serviceFee,
            transferFee,
            total: senderBase + fee,
            exchangeRate: senderBase > 0 ? ticketValue / senderBase : 1,
        }
    }

    const purchase = async (event) => {
        const quote = quoteFor(event)
        if (balance < quote.total) {
            setError(`Insufficient ${senderCurrency} wallet balance for this ticket.`)
            return
        }

        setBuyingId(String(event.id))
        setError('')
        setSuccess('')
        try {
            const response = await api.post(`/events/${event.id}/purchase`, {
                quantity: quote.quantity,
                sender_currency: senderCurrency,
                sender_amount: Number(quote.senderBase.toFixed(2)),
                fee: Number(quote.fee.toFixed(2)),
                total_deducted: Number(quote.total.toFixed(2)),
                exchange_rate: Number(quote.exchangeRate.toFixed(8)),
            })
            setSuccess(`Ticket purchased. Ref ${response.data?.reference || response.data?.order?.order_no || event.title}`)
            await wallet?.refreshWallet?.()
            await loadEvents()
        } catch (e) {
            setError(e?.response?.data?.message || 'Ticket purchase failed.')
        } finally {
            setBuyingId('')
        }
    }

    const activeEvents = useMemo(() => events, [events])

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 34, md: 46 } }}>Tickets</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1 }}>
                                Upcoming event tickets with wallet payment, receipt proof, and validation-ready records.
                            </Typography>
                        </Box>
                        <Chip label={`Wallet ${money(balance, senderCurrency)}`} sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, bgcolor: 'rgba(255,138,0,0.14)', color: ORANGE, fontWeight: 900 }} />
                    </Stack>

                    {error ? <Alert severity="error">{error}</Alert> : null}
                    {success ? <Alert severity="success">{success}</Alert> : null}

                    {loading ? (
                        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
                    ) : activeEvents.length === 0 ? (
                        <Card sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900 }}>No upcoming tickets available.</Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        <Stack spacing={2}>
                            {activeEvents.map((event) => {
                                const quote = quoteFor(event)
                                const startsAt = event.starts_at || event.date
                                return (
                                    <Card key={event.id} sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5 }}>
                                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                                                <Box sx={{ flex: 1 }}>
                                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                                        <ConfirmationNumberIcon sx={{ color: ORANGE }} />
                                                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>{event.title}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, color: 'rgba(255,255,255,0.66)' }}>
                                                        <EventIcon fontSize="small" />
                                                        <Typography>{startsAt ? new Date(startsAt).toLocaleString() : 'Date to be confirmed'}</Typography>
                                                    </Stack>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.66)', mt: 1 }}>{event.venue_name || event.venue || event.city || event.country}</Typography>
                                                </Box>

                                                <Stack spacing={1.2} sx={{ minWidth: { md: 320 } }}>
                                                    <TextField
                                                        type="number"
                                                        label="Quantity"
                                                        value={quote.quantity}
                                                        onChange={(e) => setQuantityById((current) => ({ ...current, [event.id]: Math.max(1, Number(e.target.value || 1)) }))}
                                                        inputProps={{ min: 1 }}
                                                        sx={{ '& .MuiInputBase-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.62)' } }}
                                                    />
                                                    <Row label="Ticket value" value={money(quote.ticketValue, quote.eventCurrency)} />
                                                    <Row label="Wallet charge before fee" value={money(quote.senderBase, senderCurrency)} />
                                                    <Row label="Service fee" value={money(quote.serviceFee, senderCurrency)} />
                                                    {quote.transferFee > 0 ? <Row label="Transfer fee" value={money(quote.transferFee, senderCurrency)} /> : null}
                                                    <Row label="Total payable" value={money(quote.total, senderCurrency)} accent />
                                                    <Button
                                                        variant="contained"
                                                        disabled={buyingId === String(event.id) || balance < quote.total}
                                                        onClick={() => purchase(event)}
                                                        sx={{ bgcolor: ORANGE, color: '#080C14', fontWeight: 900, '&:hover': { bgcolor: '#ff9f2e' } }}
                                                    >
                                                        {buyingId === String(event.id) ? 'Processing...' : 'Confirm Payment'}
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                )
                            })}
                        </Stack>
                    )}
                </Stack>
            </Container>
        </Box>
    )
}
